import { useState, useMemo } from "react";
import { Calculator, ShieldAlert, ShieldCheck, Scale } from "lucide-react";
import Header from "../../components/Header";
import Footer from "../../components/Footer";
import Reveal, { SectionLabel } from "../../components/Reveal";

const inputCls = "mt-2 w-full rounded-xl border border-line bg-ink px-4 py-3 font-mono text-sm text-white outline-none transition-colors focus:border-signal";

function ConsistencyChecker() {
  const [total, setTotal] = useState("5000");
  const [bestDay, setBestDay] = useState("1500");
  const [rule, setRule] = useState("40");

  const result = useMemo(() => {
    const t = parseFloat(total);
    const b = parseFloat(bestDay);
    const r = parseFloat(rule);
    if (!t || t <= 0 || isNaN(b) || b < 0) return null;
    const share = (b / t) * 100;
    const maxAllowed = (r / 100) * t;
    return { share, pass: share <= r, maxAllowed };
  }, [total, bestDay, rule]);

  return (
    <div className="card-violet p-7 sm:p-9" data-testid="consistency-checker">
      <div className="flex items-center gap-3">
        <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-signal/10"><Scale size={20} className="text-signal" /></div>
        <div>
          <h2 className="font-display text-2xl font-800 tracking-tight text-white">Consistency Rule Checker</h2>
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted">Best-day share vs firm rules</p>
        </div>
      </div>
      <div className="mt-7 grid gap-5 sm:grid-cols-3">
        <div>
          <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted">Total Profit ($)</label>
          <input type="number" min="0" data-testid="cc-total-input" value={total} onChange={(e) => setTotal(e.target.value)} className={inputCls} />
        </div>
        <div>
          <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted">Best Day Profit ($)</label>
          <input type="number" min="0" data-testid="cc-bestday-input" value={bestDay} onChange={(e) => setBestDay(e.target.value)} className={inputCls} />
        </div>
        <div>
          <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted">Firm Rule</label>
          <select data-testid="cc-rule-select" value={rule} onChange={(e) => setRule(e.target.value)} className={inputCls}>
            <option value="30">30% — strict firms</option>
            <option value="40">40% — common rule</option>
            <option value="45">45% — relaxed rule</option>
            <option value="50">50% — loose rule</option>
          </select>
        </div>
      </div>
      {result && (
        <div className={`mt-7 flex flex-wrap items-center justify-between gap-5 rounded-2xl border p-6 ${result.pass ? "border-profit/40 bg-profit/[0.06]" : "border-loss/40 bg-loss/[0.06]"}`} data-testid="cc-result">
          <div className="flex items-center gap-4">
            {result.pass ? <ShieldCheck size={30} className="text-profit" /> : <ShieldAlert size={30} className="text-loss" />}
            <div>
              <p className={`font-display text-2xl font-800 ${result.pass ? "text-profit" : "text-loss"}`} data-testid="cc-status">
                {result.pass ? "RULE PASSED" : "RULE BROKEN"}
              </p>
              <p className="mt-1 font-mono text-[11px] text-muted">
                Best day is <span className="text-white">{result.share.toFixed(1)}%</span> of total profit (limit {rule}%)
              </p>
            </div>
          </div>
          <div className="text-right">
            <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-muted">Max allowed best day</p>
            <p className="mt-1 font-mono text-2xl font-700 text-white" data-testid="cc-max-allowed">${result.maxAllowed.toFixed(0)}</p>
          </div>
        </div>
      )}
      <p className="mt-5 font-mono text-[10px] leading-relaxed text-muted">
        TIP — If your best day is too large, keep trading smaller days to raise total profit until the share drops under the limit.
      </p>
    </div>
  );
}

function DrawdownConverter() {
  const [size, setSize] = useState("100000");
  const [daily, setDaily] = useState("5");
  const [maxDd, setMaxDd] = useState("10");

  const result = useMemo(() => {
    const s = parseFloat(size);
    const d = parseFloat(daily);
    const m = parseFloat(maxDd);
    if (!s || s <= 0) return null;
    return { daily: isNaN(d) ? null : (d / 100) * s, max: isNaN(m) ? null : (m / 100) * s };
  }, [size, daily, maxDd]);

  return (
    <div className="card-violet p-7 sm:p-9" data-testid="drawdown-converter">
      <div className="flex items-center gap-3">
        <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-signal/10"><Calculator size={20} className="text-signal" /></div>
        <div>
          <h2 className="font-display text-2xl font-800 tracking-tight text-white">Drawdown Converter</h2>
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted">Turn percentages into dollars</p>
        </div>
      </div>
      <div className="mt-7 grid gap-5 sm:grid-cols-3">
        <div>
          <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted">Account Size ($)</label>
          <input type="number" min="0" data-testid="dc-size-input" value={size} onChange={(e) => setSize(e.target.value)} className={inputCls} />
        </div>
        <div>
          <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted">Daily Drawdown (%)</label>
          <input type="number" min="0" data-testid="dc-daily-input" value={daily} onChange={(e) => setDaily(e.target.value)} className={inputCls} />
        </div>
        <div>
          <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted">Max Drawdown (%)</label>
          <input type="number" min="0" data-testid="dc-max-input" value={maxDd} onChange={(e) => setMaxDd(e.target.value)} className={inputCls} />
        </div>
      </div>
      {result && (
        <div className="mt-7 grid gap-3 sm:grid-cols-2" data-testid="dc-result">
          <div className="rounded-2xl border border-line bg-ink/60 p-5">
            <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-muted">You can lose per day</p>
            <p className="mt-1.5 font-mono text-2xl font-700 text-gold">{result.daily == null ? "—" : `$${result.daily.toFixed(0)}`}</p>
          </div>
          <div className="rounded-2xl border border-line bg-ink/60 p-5">
            <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-muted">Total loss before breach</p>
            <p className="mt-1.5 font-mono text-2xl font-700 text-loss">{result.max == null ? "—" : `$${result.max.toFixed(0)}`}</p>
          </div>
        </div>
      )}
    </div>
  );
}

export default function Consistency() {
  return (
    <div data-testid="tools-page">
      <Header />
      <section className="relative overflow-hidden border-b border-line">
        <div className="terminal-grid absolute inset-0 opacity-50" />
        <div className="absolute -right-24 top-0 h-72 w-72 rounded-full bg-signal/15 blur-[130px]" />
        <div className="relative mx-auto max-w-7xl px-5 py-16 lg:px-8">
          <Reveal>
            <SectionLabel>Free Trading Tools</SectionLabel>
            <h1 className="mt-3 font-display text-4xl font-900 tracking-tight text-white sm:text-5xl">
              Consistency <span className="text-gradient">Calculator</span>
            </h1>
            <p className="mt-4 max-w-xl text-sm leading-relaxed text-muted">
              The two numbers that decide whether your payout clears: best-day consistency share
              and drawdown in real dollars.
            </p>
          </Reveal>
        </div>
      </section>
      <section className="mx-auto max-w-4xl space-y-8 px-5 py-14 lg:px-8">
        <Reveal><ConsistencyChecker /></Reveal>
        <Reveal delay={0.08}><DrawdownConverter /></Reveal>
      </section>
      <Footer />
    </div>
  );
}
